import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, InfoIcon } from 'lucide-react';
import { ProgressRail } from './ProgressRail';
import { StudyDetailsPanel } from './StudyDetailsPanel';
import { FitToViewport } from './FitToViewport';
import { HookScreen } from './screens/HookScreen';
import { StudyContextScreen } from './screens/StudyContextScreen';
import { PredictionScreen } from './screens/PredictionScreen';
import { ResultScreen } from './screens/ResultScreen';
import { PopulationScreen } from './screens/PopulationScreen';
import { MechanismScreen } from './screens/MechanismScreen';
import { CloseScreen } from './screens/CloseScreen';
import { screens } from '../data/study';
import { useLayout } from '../contexts/LayoutContext';
import { EASE } from '../utils/motion';
export function SnakExperience() {
  const reduce = useReducedMotion();
  const {
    compact
  } = useLayout();
  const mainRef = useRef<HTMLElement>(null);
  const [step, setStep] = useState(0);
  const [estimate, setEstimate] = useState(0);
  const [touched, setTouched] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  useEffect(() => {
    mainRef.current?.scrollTo({
      top: 0
    });
  }, [step]);
  const next = () => setStep(s => Math.min(s + 1, screens.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const handleEstimate = (value: number) => {
    setEstimate(value);
    setTouched(true);
  };
  const renderScreen = () => {
    switch (screens[step].id) {
      case 'hook':
        return <HookScreen onNext={next} data-id="element-198" data-mp="25jz41nszvfu4" />;
      case 'context':
        return <StudyContextScreen onNext={next} data-id="element-199" data-mp="20m7bcrkizpov" />;
      case 'prediction':
        return <PredictionScreen estimate={estimate} touched={touched} onEstimate={handleEstimate} onNext={next} data-id="element-200" data-mp="20k3akihxb6st" />;
      case 'result':
        return <ResultScreen estimate={estimate} onNext={next} data-id="element-201" data-mp="2ta397zltznn8" />;
      case 'population':
        return <PopulationScreen onNext={next} data-id="element-202" data-mp="rcj73mtm0an4" />;
      case 'mechanism':
        return <MechanismScreen onNext={next} data-id="element-203" data-mp="rff6lfl3v7do" />;
      default:
        return <CloseScreen onExplore={() => setStep(1)} onDetails={() => setDetailsOpen(true)} data-id="element-204" data-mp="ve4rrucdxt7v" />;
    }
  };
  const pad = compact ? 'px-5' : 'px-12';
  return <div className="relative flex h-full w-full flex-col overflow-hidden bg-paper font-sans text-ink" data-id="element-205" data-mp="mm2w09d9h13x">
      <header className={`flex shrink-0 items-center justify-between ${pad} ${compact ? 'py-4' : 'py-6'}`} data-id="element-206" data-mp="7ok7n84hwpx0">
        <p className="flex items-center gap-2 text-sm text-ink" data-id="element-207" data-mp="coeyjbo9tfi2">
          <span className="h-3 w-3 rounded-[3px] bg-accent-fill" aria-hidden="true" data-id="element-208" data-mp="clsi0mjejpnp" />
          <span className="font-semibold tracking-[-0.01em]" data-id="element-209" data-mp="3uvuobhbdqfz8">FIBRONEER-IPF</span>
          {!compact && <span className="text-muted" data-id="element-210" data-mp="3nqh06zo4x0f">Phase 3</span>}
        </p>
        <div className={`flex items-center ${compact ? 'gap-3' : 'gap-6'}`} data-id="element-211" data-mp="1t3g9ir44trn1">
          <ProgressRail screens={screens} current={step} data-id="element-212" data-mp="1y217ejvwvz8b" />
          <button type="button" onClick={() => setDetailsOpen(true)} aria-label="Study details" className={`flex items-center gap-1.5 whitespace-nowrap rounded-full text-sm text-muted transition-colors duration-150 ease-out hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${compact ? 'h-9 w-9 justify-center' : ''}`} data-id="element-213" data-mp="1xzf0mmguhnr5">
            <InfoIcon className="h-4 w-4" aria-hidden="true" data-id="element-214" data-mp="2qv4rxunno3p6" />
            {!compact && 'Study details'}
          </button>
        </div>
      </header>

      <main ref={mainRef} className={`relative min-h-0 flex-1 overflow-hidden ${pad}`} data-id="element-215" data-mp="2lyxhbokbe40w">
        <div className="mx-auto h-full w-full max-w-6xl" data-id="element-216" data-mp="2zvt4ekxehvzy">
          <AnimatePresence mode="wait" data-id="element-217" data-mp="2vur5y6bb5nz1">
            <motion.section key={screens[step].id} aria-label={screens[step].label} className="h-full" initial={reduce ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.24,
            ease: EASE
          }} data-id="element-218" data-mp="2vvsdw1x11ucj">
              <FitToViewport data-id="element-219" data-mp="17vey1toqmy6e">{renderScreen()}</FitToViewport>
            </motion.section>
          </AnimatePresence>
        </div>
      </main>

      <footer className={`flex shrink-0 items-center gap-4 border-t border-hairline ${pad} ${compact ? 'py-3' : 'py-4'}`} data-id="element-220" data-mp="1gxo5ubsmgdg3">
        <button type="button" onClick={back} disabled={step === 0} aria-label="Previous screen" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition-[color,background-color,opacity] duration-150 ease-out hover:bg-ink/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-0" data-id="element-221" data-mp="k8oxkws1fbnw">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-222" data-mp="5irh730cnq99" />
        </button>
        <p className={`leading-relaxed text-muted ${compact ? 'text-[11px]' : 'text-xs'}`} data-id="element-223" data-mp="5h6id1705l5l">{screens[step].footnote}</p>
      </footer>

      <StudyDetailsPanel open={detailsOpen} onClose={() => setDetailsOpen(false)} data-id="element-224" data-mp="3p5jllq370z9j" />
    </div>;
}